import { createContext, useState, useEffect, useContext, useCallback } from 'react';

const WordPressContext = createContext({});

export function WordPressProvider({ children }) {
  const [wordpressUrl, setWordpressUrl] = useState(null);
  const [returnUrl, setReturnUrl] = useState(null);
  const [isInWordPress, setIsInWordPress] = useState(false);
  const [wpRestSession, setWpRestSession] = useState(null);
  const [wpRestSessionExpiry, setWpRestSessionExpiry] = useState(null);

  useEffect(() => {
    // Check if running in iframe
    const inIframe = window.self !== window.top;
    setIsInWordPress(inIframe);

    if (inIframe) {
      // Listen for WordPress context from parent
      const handleMessage = (event) => {
        if (event.data.type === 'wordpress-context') {
          setWordpressUrl(event.data.wordpressUrl);
          setReturnUrl(event.data.returnUrl);
          
          // Store in sessionStorage for persistence
          sessionStorage.setItem('vfr-wordpress-url', event.data.wordpressUrl);
          sessionStorage.setItem('vfr-return-url', event.data.returnUrl);
          
          console.log('WordPress context received:', event.data);
        }
      };

      window.addEventListener('message', handleMessage);

      // Try to restore from sessionStorage
      const storedWpUrl = sessionStorage.getItem('vfr-wordpress-url');
      const storedReturnUrl = sessionStorage.getItem('vfr-return-url');
      if (storedWpUrl) {
        setWordpressUrl(storedWpUrl);
        setReturnUrl(storedReturnUrl);
      }

      try {
        const storedRestSession = sessionStorage.getItem('vfr-wp-rest-session');
        const storedExpiryRaw = sessionStorage.getItem('vfr-wp-rest-session-expiry');
        let parsedExpiry = null;

        if (storedExpiryRaw) {
          const numericExpiry = parseInt(storedExpiryRaw, 10);
          if (!Number.isNaN(numericExpiry)) {
            if (numericExpiry > Date.now()) {
              parsedExpiry = numericExpiry;
            } else {
              sessionStorage.removeItem('vfr-wp-rest-session');
              sessionStorage.removeItem('vfr-wp-rest-session-expiry');
            }
          }
        }

        if (storedRestSession && (!parsedExpiry || parsedExpiry > Date.now())) {
          setWpRestSession(storedRestSession);
          setWpRestSessionExpiry(parsedExpiry);
        }
      } catch (e) {
        console.warn('Failed to restore WordPress REST session from storage', e);
      }

      return () => window.removeEventListener('message', handleMessage);
    }
  }, []);

  const persistWpRestSession = useCallback((token, ttlSeconds) => {
    try {
      if (!token) {
        sessionStorage.removeItem('vfr-wp-rest-session');
        sessionStorage.removeItem('vfr-wp-rest-session-expiry');
        setWpRestSession(null);
        setWpRestSessionExpiry(null);
        return;
      }

      sessionStorage.setItem('vfr-wp-rest-session', token);
      setWpRestSession(token);

      if (typeof ttlSeconds === 'number' && ttlSeconds > 0) {
        const expiry = Date.now() + ttlSeconds * 1000;
        sessionStorage.setItem('vfr-wp-rest-session-expiry', String(expiry));
        setWpRestSessionExpiry(expiry);
      } else {
        sessionStorage.removeItem('vfr-wp-rest-session-expiry');
        setWpRestSessionExpiry(null);
      }
    } catch (e) {
      console.warn('Failed to persist WordPress REST session', e);
    }
  }, [setWpRestSession, setWpRestSessionExpiry]);

  const clearWpRestSession = useCallback(() => {
    persistWpRestSession(null, null);
  }, [persistWpRestSession]);

  const setWordPressContextValue = useCallback(({ url, backUrl }) => {
    if (url) {
      setWordpressUrl(url);
      sessionStorage.setItem('vfr-wordpress-url', url);
    }
    if (backUrl) {
      setReturnUrl(backUrl);
      sessionStorage.setItem('vfr-return-url', backUrl);
    }
  }, [setWordpressUrl, setReturnUrl]);

  const value = {
    wordpressUrl,
    returnUrl,
    isInWordPress,
    wpRestSession,
    wpRestSessionExpiry,
    getReturnUrl: (route = '/subscription') => {
      if (!isInWordPress || !wordpressUrl) {
        return `${window.location.origin}${route}`;
      }
      return `${wordpressUrl}/wp-admin/admin.php?page=vfr-admin&route=${route}`;
    },
    setWordPressContext: setWordPressContextValue,
    setWpRestSession: persistWpRestSession,
    clearWpRestSession,
  };

  return (
    <WordPressContext.Provider value={value}>
      {children}
    </WordPressContext.Provider>
  );
}

export function useWordPress() {
  const context = useContext(WordPressContext);
  if (!context) {
    throw new Error('useWordPress must be used within WordPressProvider');
  }
  return context;
}

